import { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

// Shown once, right after a society is created. The API returns the default
// SOCIETY_ADMIN login's temporaryPassword only in the create response — it is
// never returned again — so the modal only closes from its own button.

const copyText = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        // Clipboard API is unavailable on non-secure origins — fall back.
        const el = document.createElement('textarea');
        el.value = text;
        document.body.appendChild(el);
        el.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(el);
        return ok;
    }
};

const CredentialRow = ({ label, value, copied, onCopy }) => (
    <div>
        <div className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-400 tw-font-semibold tw-mb-0.5">{label}</div>
        <div className="tw-flex tw-items-center tw-gap-2 tw-px-2.5 tw-py-1.5 tw-rounded-md tw-bg-gray-50 tw-border tw-border-gray-200">
            <span className="tw-flex-1 tw-min-w-0 tw-truncate tw-font-mono tw-text-xs tw-text-gray-800">{value}</span>
            <button
                type="button"
                onClick={onCopy}
                className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium tw-border ${copied
                    ? 'tw-text-emerald-700 tw-bg-emerald-50 tw-border-emerald-200'
                    : 'tw-text-gray-600 tw-bg-white tw-border-gray-200 hover:tw-bg-gray-100'}`}
            >
                <Icon icon={copied ? 'mdi:check' : 'mdi:content-copy'} className="tw-text-xs" />
                {copied ? 'Copied' : 'Copy'}
            </button>
        </div>
    </div>
);

const CreatedAdminModal = ({ admin, societyName, onClose }) => {
    const [copiedKey, setCopiedKey] = useState(null);

    const handleCopy = async (key, text) => {
        if (await copyText(text)) {
            setCopiedKey(key);
            setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
        }
    };

    return (
        <Dialog
            open={Boolean(admin)}
            // Backdrop click / Escape would lose the password for good.
            onClose={() => {}}
            disableEscapeKeyDown
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <DialogTitle component="div" sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <span className="tw-w-7 tw-h-7 tw-rounded-full tw-bg-emerald-100 tw-flex tw-items-center tw-justify-center">
                        <Icon icon="mdi:check" className="tw-text-emerald-600 tw-text-sm" />
                    </span>
                    <div>
                        <div className="tw-text-sm tw-font-semibold tw-text-gray-800">Society created</div>
                        {societyName && <div className="tw-text-[11px] tw-text-gray-500">{societyName}</div>}
                    </div>
                </div>
            </DialogTitle>

            <DialogContent sx={{ px: 2, py: 1.5, '&.MuiDialogContent-root': { pt: 1.5 } }}>
                <p className="tw-text-xs tw-text-gray-600 tw-mb-3">
                    A default society admin login was created. Share these details with the society admin.
                </p>
                {admin && (
                    <div className="tw-space-y-2.5">
                        <CredentialRow label="Admin email" value={admin.email}
                            copied={copiedKey === 'email'} onCopy={() => handleCopy('email', admin.email)} />
                        <CredentialRow label="Temporary password" value={admin.temporaryPassword}
                            copied={copiedKey === 'password'} onCopy={() => handleCopy('password', admin.temporaryPassword)} />
                    </div>
                )}
                <div className="tw-flex tw-items-start tw-gap-1.5 tw-mt-3 tw-px-2.5 tw-py-1.5 tw-rounded-md tw-bg-amber-50 tw-border tw-border-amber-200 tw-text-[11px] tw-text-amber-800">
                    <Icon icon="mdi:alert-outline" className="tw-text-sm tw-flex-shrink-0 tw-mt-px" />
                    Record the password now — it will not be shown again.
                </div>
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
                <button
                    type="button"
                    onClick={onClose}
                    className="tw-h-8 tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700"
                >
                    <Icon icon="mdi:check" className="tw-text-sm" />
                    I've saved these details
                </button>
            </DialogActions>
        </Dialog>
    );
};

export default CreatedAdminModal;
